'use client';

import React, { useCallback, useMemo } from 'react';
import type { CartItemV2Props } from './cart-item-v2.types';
import { cn } from '../../utils/cn';
import Image from '../../atoms/image';
import { Text } from '../../atoms/text';
import { ProductOptions } from '../../molecules/product-options/product-options';
import { PriceBox } from '../../molecules/catalog';
import { usePrice } from '../../utils/use-price';
import { Link } from '../../atoms/link';
import { getCartItemImage, getProductUrl } from './cart-item-v2.helpers';

import styles from './cart-item-v2.module.css';
import Button from '../../atoms/button';
import { CartItemV2VariantEnum } from './cart-item-v2.enum';
import { CartCounterSection } from '../../molecules/cart-counter-section';

/**
 * CartItemV2 - Base UI component for cart item rendering
 *
 * This is the UI Core base component matching mobile ui-react-native-core CartItemV2.
 * It handles:
 * - Cart item image with out of stock overlay
 * - Cart item name and options
 * - Price display
 * - Counter button or quantity display
 * - Additional info component slot
 *
 * @see packages/ui-react-native-core/src/components/organisms/cart-v2/cart-item-v2/cart-item-v2.tsx
 */
export const CartItemV2: React.FC<CartItemV2Props> = ({
    testId = 'cart-item',
    item,
    count,
    disabled,
    dataTestIdInformation = 'information',
    dataTestIdTotal = 'total',
    dataTestIdPriceBox = 'price-box',
    cartCounterButtonProps,
    onItemPressed,
    cartItemReference,
    outOfStockMessage,
    errorMessage,
    CartItemAdditionalInfoComponent,
    onDelete,
    disabledButtonDelete = false,
    buttonProps,
    variantCounterButton,
    variantSizePriceBox,
    variantCartItemGroup,
    variant = CartItemV2VariantEnum.SMALL,
    showOnlySelected,
}) => {
    const itemQuantity = item?.quantity ?? 0;
    const formatNumber = String(count ?? itemQuantity).padStart(2, '0');

    // Extract product data from item
    const productForPriceBox = item?.configured_variant ?? item?.product;

    const productImage = getCartItemImage(item);

    const productName = item?.product?.name ?? '';

    const totalPrice = usePrice({
        amount: item?.prices?.row_total?.value ?? 0,
    });

    const showActions = useMemo(() => {
        if (variant !== CartItemV2VariantEnum.BIG) return false;

        return !!onDelete;
    }, [variant, onDelete]);

    const containerClass = useMemo(
        () => cn(styles.container, variant === CartItemV2VariantEnum.SMALL ? styles.small : styles.big),
        [variant]
    );

    const getTestId = useCallback((suffix?: string) => (testId ? `${testId}-${suffix}` : undefined), [testId]);

    const actions = useMemo(() => {
        if (!showActions) return null;

        const totalPriceClass = cn(styles.total, disabled && styles.disabled);

        return (
            <div className={styles.totalPriceClassName}>
                <Text data-testid={getTestId(dataTestIdTotal)} showIf={!!totalPrice?.price} className={totalPriceClass}>
                    {totalPrice.price}
                </Text>

                <Button
                    showIf={onDelete && !disabledButtonDelete}
                    onClick={onDelete}
                    className={cn(styles.bigDelete, buttonProps?.className)}
                    {...buttonProps}
                />
            </div>
        );
    }, [
        showActions,
        disabled,
        totalPrice.price,
        dataTestIdTotal,
        getTestId,
        onDelete,
        disabledButtonDelete,
        buttonProps,
    ]);

    const additionalInfo = useMemo(() => {
        if (!CartItemAdditionalInfoComponent) return null;

        return (
            <div className={styles.additionalInfo}>
                <CartItemAdditionalInfoComponent
                    testId={testId}
                    item={item}
                    count={count}
                    disabled={disabled}
                    cartCounterButtonProps={cartCounterButtonProps}
                    onItemPressed={onItemPressed}
                    cartItemReference={cartItemReference}
                    outOfStockMessage={outOfStockMessage}
                />
            </div>
        );
    }, [
        CartItemAdditionalInfoComponent,
        testId,
        item,
        count,
        disabled,
        cartCounterButtonProps,
        onItemPressed,
        cartItemReference,
        outOfStockMessage,
    ]);
    const linkClass = useMemo(() => cn(styles.disabledLink), []);

    const imageLinkClass = useMemo(
        () => cn(styles.cartItemImage, showOnlySelected && linkClass),
        [showOnlySelected, linkClass]
    );

    const nameLinkClass = useMemo(
        () => cn(styles.nameButton, showOnlySelected && linkClass),
        [showOnlySelected, linkClass]
    );

    const itemInformationClass = useMemo(() => cn(styles.itemInfomation, disabled && styles.disabled), [disabled]);

    const priceBoxClass = useMemo(() => cn(styles.priceBox, disabled && styles.disabled), [disabled]);

    return (
        <div data-testid={testId} className={containerClass}>
            <div className={styles.content}>
                {/* Image */}
                <div className={styles.imageWrapper}>
                    <Link className={imageLinkClass} href={getProductUrl(item?.product)}>
                        <Image src={productImage} alt={productName} fill className={styles.image} />

                        {disabled && outOfStockMessage && (
                            <div className={styles.outOfStockOverlay}>
                                <Text size="small" className={styles.outOfStockText}>
                                    {outOfStockMessage}
                                </Text>
                            </div>
                        )}
                    </Link>
                </div>

                {/* Right Content */}
                <div className={styles.right}>
                    <div className={styles.rightSection}>
                        {/* Info */}
                        <div data-testid={getTestId(dataTestIdInformation)} className={itemInformationClass}>
                            {/* Cart Item Name */}
                            <Link className={nameLinkClass} href={getProductUrl(item?.product)}>
                                <Text className={styles.name}>{productName}</Text>
                            </Link>

                            {/* Product Options - configurable options */}
                            <ProductOptions item={item} />
                        </div>

                        {/* Price and Counter Row */}
                        <div className={styles.itemContent}>
                            <div data-testid={getTestId(dataTestIdPriceBox)} className={priceBoxClass}>
                                <PriceBox product={productForPriceBox} variantSize={variantSizePriceBox} />
                            </div>

                            <CartCounterSection
                                item={item}
                                cartItemReference={cartItemReference}
                                disabled={disabled}
                                onDelete={onDelete}
                                variantCartItemGroup={variantCartItemGroup}
                                variantCounterButton={variantCounterButton}
                                buttonProps={buttonProps}
                                cartCounterButtonProps={cartCounterButtonProps}
                            />

                            <Text showIf={!cartCounterButtonProps} className={styles.textCount}>
                                x {formatNumber}
                            </Text>

                            {actions}
                        </div>
                    </div>

                    {/* Additional Info Component */}
                    {additionalInfo}

                    {/* Per-item error message (e.g., gift not salable) */}
                    {errorMessage && (
                        <div className={styles.errorMessage}>
                            <Text size="small" className={styles.errorText}>
                                {errorMessage}
                            </Text>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
